import { toast } from "react-toastify";
import { getPosts } from "../../apis/posts";
import {
    normalizePostsResponse,
    getPostDate,
} from "../../utils/normalizePost";
import { useCursorPaginatedList, mergeById } from "../../hooks/useCursorPaginatedList";
import { useUrlListFilters } from "../../hooks/useUrlListFilters";
import FilterBar from "../shared/FilterBar";
import ListScrollFooter from "../shared/ListScrollFooter";
import EmptyListState from "../shared/EmptyListState";
import PostCard from "../shared/PostCard";
import { DividedList, DividedListItem } from "../shared/ContentList";
import PageHeader, { PageShell } from "../shared/PageHeader";
import PostListSkeletons from "./PostListSkeletons";
import { APP_ROUTES } from "../../constants/appRoutes";
import "bootstrap-icons/font/bootstrap-icons.css";

const LOAD_MORE_SKELETON_COUNT = 3;

function formatPostDate(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

function applyClientFilters(posts, { startDate, endDate }) {
    let filtered = posts;

    if (startDate) {
        const from = new Date(startDate);
        filtered = filtered.filter((post) => {
            const created = getPostDate(post);
            return created && new Date(created) >= from;
        });
    }

    if (endDate) {
        const to = new Date(endDate);
        to.setHours(23, 59, 59, 999);
        filtered = filtered.filter((post) => {
            const created = getPostDate(post);
            return created && new Date(created) <= to;
        });
    }

    return filtered;
}

export default function MyPosts() {
    const {
        limit,
        startDate,
        endDate,
        setLimit,
        setStartDate,
        setEndDate,
    } = useUrlListFilters();

    const {
        items: posts,
        hasMore,
        loading,
        loadingMore,
        error,
        loadMoreRef,
    } = useCursorPaginatedList({
        fetchFn: getPosts,
        normalizeResponse: normalizePostsResponse,
        applyClientFilters,
        mergeItems: mergeById,
        filters: { limit, startDate, endDate },
    });

    const copyText = async (text, message) => {
        if (!text) return;

        try {
            await navigator.clipboard.writeText(text);
            toast(message);
        } catch {
            toast.error("Unable to copy right now");
        }
    };

    return (
        <PageShell>
            <PageHeader title="My Posts" description="View and manage your saved posts." />

            <FilterBar
                limit={limit}
                onLimitChange={setLimit}
                startDate={startDate}
                onStartDateChange={setStartDate}
                endDate={endDate}
                onEndDateChange={setEndDate}
            />

            {error ? (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                    <p className="mb-0 font-semibold">Could not load posts</p>
                    <p className="mt-1 text-sm">{error}</p>
                </div>
            ) : null}

            {loading ? (
                <PostListSkeletons count={limit} />
            ) : posts.length === 0 ? (
                <EmptyListState
                    message={
                        startDate || endDate
                            ? "No posts match your filters."
                            : "No posts to show yet."
                    }
                    actionLabel={
                        !startDate && !endDate
                            ? "Create your first post"
                            : undefined
                    }
                    actionTo={APP_ROUTES.CAMPAIGN_WIZARD}
                />
            ) : (
                <DividedList>
                    {posts.map((post, index) => {
                        const id = post.id ?? index;
                        const createdLabel = formatPostDate(getPostDate(post));

                        return (
                            <DividedListItem key={id}>
                                <PostCard
                                    post={post}
                                    onCopy={copyText}
                                    createdLabel={createdLabel}
                                    campaignId={post.campaignId}
                                />
                            </DividedListItem>
                        );
                    })}
                </DividedList>
            )}

            {loadingMore ? (
                <PostListSkeletons
                    count={LOAD_MORE_SKELETON_COUNT}
                    label="Loading more posts"
                    className="border-t border-slate-200"
                />
            ) : null}

            <ListScrollFooter
                loadMoreRef={loadMoreRef}
                showAllShown={!loading && !loadingMore && posts.length > 0 && !hasMore}
            />
        </PageShell>
    );
}
